import * as Sentry from "@sentry/react";
import { QueryClient } from "@tanstack/react-query";
import { HttpError } from "./httpError";

const MAX_QUERY_RETRIES = 3;
const MAX_RETRY_DELAY_MS = 10000;

const isRetryableError = (error: unknown) => {
  if (error instanceof HttpError) {
    if (typeof error.status === "number") {
      if (error.status === 429) return true;
      return error.status >= 500;
    }
  }
  return true;
};

const retryDelay = (attemptIndex: number) => {
  const delay = 1000 * 2 ** attemptIndex;
  return Math.min(delay, MAX_RETRY_DELAY_MS);
};

const safeStringify = (value: unknown) => {
  try {
    return JSON.stringify(value);
  } catch {
    return "[unserializable]";
  }
};

const captureQueryError = (error: unknown) => {
  Sentry.withScope((scope) => {
    scope.setLevel("error");
    if (error instanceof HttpError) {
      scope.setTag("http.status", String(error.status ?? ""));
      scope.setTag("http.url", error.url ?? "");
      scope.setContext("http", {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
      });
    }
    Sentry.captureException(error);
  });
};

const captureMutationError = (error: unknown, variables: unknown, context: unknown) => {
  Sentry.withScope((scope) => {
    scope.setLevel("error");
    if (error instanceof HttpError) {
      scope.setTag("http.status", String(error.status ?? ""));
      scope.setTag("http.url", error.url ?? "");
      scope.setContext("http", {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
      });
    }
    scope.setExtra("variables", safeStringify(variables));
    scope.setExtra("context", safeStringify(context));
    Sentry.captureException(error);
  });
};

export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        networkMode: "always",
        retry: (failureCount, error) => failureCount < MAX_QUERY_RETRIES && isRetryableError(error),
        retryDelay,
        onError: captureQueryError,
      },
      mutations: {
        networkMode: "always",
        onError: captureMutationError,
      },
    },
  });
